<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const store = useStore();
const { t } = useI18n();

const venue = computed(
  () =>
    store.state.onboarding.reviewVenue || {
      name: '',
      address: '',
      city: '',
      country: '',
      website: '',
    }
);
const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);

const canContinue = computed(() => Boolean((venue.value.name || '').trim()));

function updateField(field, value) {
  store.dispatch('onboarding/updateReviewVenue', { [field]: value });
}

const continueToSpaces = () => store.dispatch('onboarding/saveVenue');

useRegisterOnboardingNav(() => ({
  primary: {
    label: t('ONBOARDING_WIZARD.CONTINUE'),
    disabled: !canContinue.value,
    loading: uiFlags.value.isSaving,
    onClick: continueToSpaces,
  },
}));
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.PHASES.2.LABEL')"
    :title="t('ONBOARDING_WIZARD.VENUE_REVIEW.TITLE')"
    :description="t('ONBOARDING_WIZARD.VENUE_REVIEW.DESCRIPTION')"
  >
    <div class="w-full flex flex-col gap-4">
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('ONBOARDING_WIZARD.VENUE_REVIEW.NAME_LABEL') }}
        </span>
        <input
          :value="venue.name"
          type="text"
          class="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          :placeholder="t('ONBOARDING_WIZARD.VENUE_REVIEW.NAME_PLACEHOLDER')"
          @input="updateField('name', $event.target.value)"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('ONBOARDING_WIZARD.VENUE_REVIEW.ADDRESS_LABEL') }}
        </span>
        <input
          :value="venue.address"
          type="text"
          class="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          :placeholder="t('ONBOARDING_WIZARD.VENUE_REVIEW.ADDRESS_PLACEHOLDER')"
          @input="updateField('address', $event.target.value)"
        />
      </label>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('ONBOARDING_WIZARD.VENUE_REVIEW.CITY_LABEL') }}
          </span>
          <input
            :value="venue.city"
            type="text"
            class="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            :placeholder="t('ONBOARDING_WIZARD.VENUE_REVIEW.CITY_PLACEHOLDER')"
            @input="updateField('city', $event.target.value)"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('ONBOARDING_WIZARD.VENUE_REVIEW.COUNTRY_LABEL') }}
          </span>
          <input
            :value="venue.country"
            type="text"
            class="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            :placeholder="
              t('ONBOARDING_WIZARD.VENUE_REVIEW.COUNTRY_PLACEHOLDER')
            "
            @input="updateField('country', $event.target.value)"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('ONBOARDING_WIZARD.VENUE_REVIEW.WEBSITE_LABEL') }}
        </span>
        <input
          :value="venue.website"
          type="url"
          class="w-full h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          :placeholder="t('ONBOARDING_WIZARD.VENUE_REVIEW.WEBSITE_PLACEHOLDER')"
          @input="updateField('website', $event.target.value)"
        />
      </label>
    </div>
  </OnboardingStepLayout>
</template>
